import React from "react";
import {
    Button,
    Header,
    Icon,
    Label,
    List,
    Modal,
    ModalActions,
    ModalContent,
    Segment,
    Table,
} from "semantic-ui-react";
import { DetailedOrder } from "../../../types/models/Reporting";
import {
    formatCurrency,
    formatPercent,
    formatReportAddress,
    formatReportDate,
    orderStatusColor,
    orderStatusLabel,
    paymentTypeLabel,
    riskColor,
    riskLabel,
} from "../reportingFormatters";
import AddressMapModal from "../../orders/modals/AddressMapModal";
import GuaranteeFilesModal from "../../guarantees/modals/GuaranteeFilesModal";
import {
    GUARANTEE_STATUS_COMPLETED,
    GUARANTEE_STATUS_LABELS,
    GUARANTEE_STATUS_OPEN,
    GUARANTEE_TYPE_LABELS,
} from "../../../utils/constants";

interface Props {
    order?: DetailedOrder;
    onClose: () => void;
}

const DetailField = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <div className="report-detail-field">
        <span>{label}</span>
        <strong>{value || "—"}</strong>
    </div>
);

const OrderReportDetailModal = ({ order, onClose }: Props) => {
    if (!order) return null;
    const management = order.management;

    return (
        <Modal
            closeIcon
            closeOnDimmerClick
            size="large"
            open
            onClose={onClose}
            className="dashboard-inline-close-modal"
        >
            <Header>
                <Icon name="file alternate outline" />
                <Header.Content>
                    Pedido #{order.invoice || "sin factura"}
                    <Header.Subheader>{order.id}</Header.Subheader>
                </Header.Content>
            </Header>
            <ModalContent scrolling>
                <div className="report-detail-hero">
                    <div>
                        <Label color={riskColor(management.riskLevel)} size="large">
                            {riskLabel(management.riskLevel)} · {management.priorityScore} puntos
                        </Label>
                        <h3>{management.nextAction}</h3>
                        <div className="dashboard-label-list">
                            {management.reasons.map((reason) => <Label basic color="orange" key={reason}>{reason}</Label>)}
                        </div>
                    </div>
                    <div className="report-payment-progress">
                        <span>Avance de cobranza</span>
                        <strong>{formatPercent(management.paymentProgressPercent)}</strong>
                        <small>{formatCurrency(order.paidAmount)} de {formatCurrency(order.totalAmount)}</small>
                    </div>
                </div>

                <div className="report-detail-grid">
                    <DetailField label="Fecha de creación" value={formatReportDate(order.createdAt, true)} />
                    <DetailField label="Última actualización" value={formatReportDate(order.updatedAt, true)} />
                    <DetailField
                        label="Estado actual"
                        value={order.currentStatus && (
                            <Label color={orderStatusColor(order.currentStatus.code)}>
                                {orderStatusLabel(order.currentStatus.code, order.currentStatus.label)}
                            </Label>
                        )}
                    />
                    <DetailField label="Inicio del estado" value={formatReportDate(order.currentStatus?.startDate, true)} />
                    <DetailField label="Días en el estado" value={`${management.daysInCurrentStatus} días`} />
                    <DetailField label="Método de pago" value={order.paymentMethod?.label} />
                    <DetailField label="Importe total" value={formatCurrency(order.totalAmount)} />
                    <DetailField label="Total pagado" value={formatCurrency(order.paidAmount)} />
                    <DetailField label="Saldo pendiente" value={formatCurrency(order.outstandingBalance)} />
                    <DetailField label="Tiene comprobante" value={order.hasPaymentProof ? "Sí" : "No"} />
                    <DetailField label="Recordatorio" value={formatReportDate(order.reminderDate)} />
                    <DetailField label="Instalación" value={formatReportDate(order.installationDate, true)} />
                </div>

                <div className="report-detail-columns">
                    <Segment>
                        <Header as="h4"><Icon name="user outline" /> Personas</Header>
                        <DetailField label="Cliente" value={order.client?.fullName} />
                        <DetailField label="Teléfono" value={order.client?.phone} />
                        <DetailField label="Asesor" value={order.advisor?.fullName} />
                        <DetailField label="Instalador" value={order.installer?.fullName} />
                    </Segment>
                    <Segment>
                        <Header as="h4"><Icon name="shopping bag" /> Producto y operación</Header>
                        <DetailField label="Producto" value={order.product?.name} />
                        <DetailField label="SKU" value={order.product?.sku} />
                        <DetailField label="Producción configurada" value={order.product ? `${order.product.productionTimeMin}–${order.product.productionTimeMax} días` : undefined} />
                        <DetailField label="Recordatorio configurado" value={order.product ? `${order.product.reminderTime} días` : undefined} />
                    </Segment>
                </div>

                <Segment>
                    <Header as="h4"><Icon name="map marker alternate" /> Dirección de instalación</Header>
                    <p>{formatReportAddress(order.address)}</p>
                    {order.latitude != null && order.longitude != null ? (
                        <AddressMapModal
                            currentLatitude={order.latitude}
                            currentLongitude={order.longitude}
                        />
                    ) : (
                        <p className="dashboard-muted">Sin coordenadas registradas</p>
                    )}
                </Segment>

                <Segment>
                    <Header as="h4"><Icon name="history" /> Historial de estados</Header>
                    <div className="dashboard-table-scroll">
                        <Table compact basic="very" className="dashboard-table">
                            <Table.Header><Table.Row><Table.HeaderCell>Estado</Table.HeaderCell><Table.HeaderCell>Inicio</Table.HeaderCell><Table.HeaderCell>Fin</Table.HeaderCell><Table.HeaderCell>Usuario de transición</Table.HeaderCell></Table.Row></Table.Header>
                            <Table.Body>{order.statusHistory.map((status) => (
                                <Table.Row key={status.id}>
                                    <Table.Cell>
                                        <Label color={orderStatusColor(status.code)}>
                                            {orderStatusLabel(status.code, status.label)}
                                        </Label>
                                    </Table.Cell>
                                    <Table.Cell>{formatReportDate(status.startDate, true)}</Table.Cell>
                                    <Table.Cell>{formatReportDate(status.endDate, true)}</Table.Cell>
                                    <Table.Cell>{status.changedBy?.fullName || status.changedBy?.username || "Sin atribución"}</Table.Cell>
                                </Table.Row>
                            ))}</Table.Body>
                        </Table>
                    </div>
                </Segment>

                <div className="report-detail-columns">
                    <Segment>
                        <Header as="h4"><Icon name="dollar" /> Pagos ({order.payments.length})</Header>
                        {order.payments.length === 0 ? <p className="dashboard-muted">Sin pagos relacionados.</p> : (
                            <List divided relaxed>{order.payments.map((payment) => (
                                <List.Item key={payment.id}>
                                    <List.Icon name={payment.hasPaymentProof ? "check circle" : "circle outline"} color={payment.hasPaymentProof ? "green" : "grey"} />
                                    <List.Content>
                                        <List.Header>{paymentTypeLabel(payment.type)} · {formatCurrency(payment.amount)}</List.Header>
                                        <List.Description>{formatReportDate(payment.createdAt, true)} · {payment.hasPaymentProof ? "Con comprobante" : "Sin comprobante"}</List.Description>
                                    </List.Content>
                                </List.Item>
                            ))}</List>
                        )}
                    </Segment>
                    <Segment>
                        <Header as="h4"><Icon name="exchange" /> Actualizaciones externas ({order.externalUpdates.length})</Header>
                        {order.externalUpdates.length === 0 ? <p className="dashboard-muted">Sin actualizaciones externas.</p> : (
                            <List divided relaxed>{order.externalUpdates.map((update) => (
                                <List.Item key={update.id}>
                                    <List.Content>
                                        <List.Header>{update.description}</List.Header>
                                        <List.Description>{formatReportDate(update.createdAt, true)} · {update.createdBy?.fullName || "Sin atribución"}</List.Description>
                                    </List.Content>
                                </List.Item>
                            ))}</List>
                        )}
                    </Segment>
                </div>

                <Segment>
                    <Header as="h4"><Icon name="shield alternate" /> Garantías ({order.guarantees.length})</Header>
                    {order.guarantees.length === 0 ? <p className="dashboard-muted">Sin garantías relacionadas.</p> : (
                        <div className="report-guarantee-grid">{order.guarantees.map((guarantee) => {
                            const status = guarantee.completed ? GUARANTEE_STATUS_COMPLETED : GUARANTEE_STATUS_OPEN;
                            return (
                                <article key={guarantee.id}>
                                    <div>
                                        <Label color={guarantee.completed ? "green" : "orange"}>{GUARANTEE_STATUS_LABELS[status]}</Label>
                                        <strong>{GUARANTEE_TYPE_LABELS[guarantee.type] || "Tipo no especificado"}</strong>
                                    </div>
                                    <p>{guarantee.cause}</p>
                                    {guarantee.solution && <p><b>Solución:</b> {guarantee.solution}</p>}
                                    <small>{formatReportDate(guarantee.createdAt, true)} · Saldo {formatCurrency(guarantee.moneyRemaining)}</small>
                                    {guarantee.comments.length > 0 && <p><b>Comentarios:</b> {guarantee.comments.map((comment) => comment.comment).join(" · ")}</p>}
                                    {guarantee.files.length > 0 ? (
                                        <GuaranteeFilesModal fluid guarantee={guarantee} readOnly />
                                    ) : (
                                        <p className="dashboard-muted">Sin evidencias fotográficas.</p>
                                    )}
                                </article>
                            );
                        })}</div>
                    )}
                </Segment>
            </ModalContent>
            <ModalActions>
                <Button onClick={onClose}><Icon name="cancel" /> Cerrar</Button>
                <Button
                    secondary
                    href={`/orders/${order.id}/details`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Icon name="external" /> Abrir pedido
                </Button>
            </ModalActions>
        </Modal>
    );
};

export default OrderReportDetailModal;
